import React, { FC, ReactElement, useEffect, useState } from "react";
import { Typography, message, Skeleton } from "antd";
import { Link } from "react-router-dom";
import { BASE_URL } from "../../../constants/urlConstants";
import "./PerfumeCardsSlider.css";

const { Text, Paragraph } = Typography;

interface PerfumeResponse {
  id: number;
  perfumeTitle: string;
  brand: string;
  filename: string;
  price: number;
  distributorPrice?: number;
  discountPrice?: number;
  perfumeRating: number;
  unit?: string;
}

interface PerfumeCardsSliderProps {
  perfumes: { id: number }[];
}

const PerfumeCardsSlider: FC<PerfumeCardsSliderProps> = ({ perfumes }): ReactElement => {
  const [perfumeData, setPerfumeData]   = useState<PerfumeResponse[]>([]);
  const [loading, setLoading]           = useState<boolean>(false);
  const [isDistributor, setIsDistributor] = useState<boolean>(false);

  useEffect(() => {
    setIsDistributor(localStorage.getItem("role") === "3");
  }, []);

  useEffect(() => {
    if (!perfumes?.length) return;

    const fetchPerfumes = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${BASE_URL}/api/v1/perfumes/ids`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(perfumes.map((p) => p.id)),
        });
        if (!res.ok) throw new Error();
        setPerfumeData(await res.json());
      } catch {
        message.error("Unable to load featured products");
      } finally {
        setLoading(false);
      }
    };

    fetchPerfumes();
  }, [perfumes]);

  /* ── Skeleton placeholders while loading ── */
  if (loading) {
    return (
      <div className="fpc-grid">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="fpc-skeleton">
            <Skeleton.Image active className="fpc-skeleton-img" />
            <Skeleton active paragraph={{ rows: 2 }} title={false} className="fpc-skeleton-text" />
          </div>
        ))}
      </div>
    );
  }

  if (!perfumeData.length) return <></>;

  return (
    <div className="fpc-grid">
      {perfumeData.map((perfume) => {
        const retail    = perfume.price;
        const finalPrice = isDistributor
          ? (perfume.distributorPrice ?? retail)
          : (perfume.discountPrice   ?? retail);
        const hasDiscount = !isDistributor && perfume.discountPrice && perfume.discountPrice < retail;
        const discountPct = hasDiscount
          ? Math.round(((retail - perfume.discountPrice!) / retail) * 100)
          : 0;

        return (
          <Link key={perfume.id} to={`/product/${perfume.id}`} className="fpc-link">
            <div className="fpc-card">

              {/* Image */}
              <div className="fpc-img-wrap">
                <img
                  src={perfume.filename}
                  alt={perfume.perfumeTitle}
                  className="fpc-img"
                  loading="lazy"
                />

                {/* Rating badge */}
                {perfume.perfumeRating > 0 && (
                  <span className="fpc-rating">
                    {perfume.perfumeRating.toFixed(1)} ★
                  </span>
                )}

                {/* Discount badge */}
                {hasDiscount && (
                  <span className="fpc-discount">{discountPct}% off</span>
                )}
              </div>

              {/* Info */}
              <div className="fpc-info">
                {/* Fixed 2-line height so all cards align prices at same level */}
                <div className="fpc-title-wrap">
                  <Paragraph className="fpc-title" ellipsis={{ rows: 2 }}>
                    {perfume.perfumeTitle}
                  </Paragraph>
                </div>

                <Text className="fpc-brand">{perfume.brand}</Text>

               <div className="fpc-price-block">
                 <div className="fpc-price-main">
                   <Text className="fpc-price-final">₹{finalPrice}</Text>
                   {perfume.unit && (
                     <Text className="fpc-unit-label">/ per {perfume.unit.toLowerCase()}</Text>
                   )}
                 </div>

                 {(!isDistributor && perfume.discountPrice) || (hasDiscount && discountPct > 0) || isDistributor ? (
                   <div className="fpc-price-sub">
                     {!isDistributor && perfume.discountPrice && (
                       <Text className="fpc-price-old">₹{retail}</Text>
                     )}
                     {hasDiscount && discountPct > 0 && (
                       <Text className="fpc-discount-pct">{discountPct}% off</Text>
                     )}
                     {isDistributor && (
                       <Text className="fpc-dist-label">Distributor</Text>
                     )}
                   </div>
                 ) : null}
               </div>
              </div>

            </div>
          </Link>
        );
      })}
    </div>
  );
};

export default PerfumeCardsSlider;